import React, { useMemo, useState } from "react";
import { apiGet, apiPost } from "../../services/api";
import { SupplierBillRow, SupplierOption, SupplierPaymentRow } from "./types";
import { formatWhen } from "./utils";

interface ClinicVendorBillsProps {
    suppliers: SupplierOption[];
    financeDate: string;
    showToast: (args: { message: string; type: "success" | "error" | "info" | "warning" }) => void;
    onSuccess: () => Promise<void>;
}

export default function ClinicVendorBills({ suppliers, financeDate, showToast, onSuccess }: ClinicVendorBillsProps) {
    const [selectedSupplierId, setSelectedSupplierId] = useState("");
    const [supplierBills, setSupplierBills] = useState<SupplierBillRow[]>([]);
    const [loadingSupplierFinance, setLoadingSupplierFinance] = useState(false);
    const [selectedSupplierBillId, setSelectedSupplierBillId] = useState("");
    const [supplierPayAmount, setSupplierPayAmount] = useState("");
    const [supplierPayMode, setSupplierPayMode] = useState("Cash");
    const [supplierReceipt, setSupplierReceipt] = useState("");
    const [payingSupplier, setPayingSupplier] = useState(false);

    const loadSupplierFinance = async (supplierId: string) => {
        if (!supplierId) {
            setSupplierBills([]);
            setSelectedSupplierBillId("");
            return;
        }
        setLoadingSupplierFinance(true);
        try {
            const bills = (await apiGet(`/api/manager/supplier/${supplierId}/bills`)) as SupplierBillRow[];
            setSupplierBills(Array.isArray(bills) ? bills : []);
            if (Array.isArray(bills) && bills.length > 0) {
                setSelectedSupplierBillId(String(bills[0].id));
            }
        } catch {
            setSupplierBills([]);
        } finally {
            setLoadingSupplierFinance(false);
        }
    };

    const selectedSupplierBill = useMemo(
        () => supplierBills.find((bill) => String(bill.id) === selectedSupplierBillId) || null,
        [supplierBills, selectedSupplierBillId]
    );

    const handleSupplierPayment = async () => {
        const billId = Number(selectedSupplierBillId);
        const amount = Number(supplierPayAmount || 0);
        if (!selectedSupplierId) { showToast({ message: "Select supplier.", type: "warning" }); return; }
        if (!billId) { showToast({ message: "Select bill.", type: "warning" }); return; }
        if (!Number.isFinite(amount) || amount <= 0) { showToast({ message: "Enter valid payment amount.", type: "warning" }); return; }

        const balance = Number(selectedSupplierBill?.balance || 0);
        if (balance > 0 && amount > balance) { showToast({ message: "Amount exceeds bill balance.", type: "warning" }); return; }

        setPayingSupplier(true);
        try {
            await apiPost("/api/manager/supplier-payment", {
                purchase_bill_id: billId,
                amount,
                mode: supplierPayMode || "Cash",
                receipt_number: supplierReceipt.trim() || null,
                payment_date: financeDate,
            });

            showToast({ message: "Supplier payment saved.", type: "success" });
            setSupplierReceipt("");
            setSupplierPayAmount("");
            await loadSupplierFinance(selectedSupplierId);
            await onSuccess();
        } catch {
            showToast({ message: "Failed to save supplier payment.", type: "error" });
        } finally {
            setPayingSupplier(false);
        }
    };

    const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', background: '#f8fafc' };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: 'var(--mod-text-main)' }}>Clinic Vendor Bills</h3>
                <select style={{ ...inputStyle, marginBottom: '16px' }} value={selectedSupplierId} onChange={(e) => { setSelectedSupplierId(e.target.value); void loadSupplierFinance(e.target.value); }}>
                    <option value="">Select supplier</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name} {s.balance_due ? `(₹${s.balance_due})` : ""}</option>)}
                </select>

                {loadingSupplierFinance && <div style={{ color: 'var(--mod-text-muted)', fontSize: '0.85rem' }}>Loading bills...</div>}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {supplierBills.filter(b => Number(b.balance || 0) > 0).map((b) => (
                        <div key={b.id} onClick={() => setSelectedSupplierBillId(String(b.id))} style={{ padding: '12px', borderRadius: '8px', border: selectedSupplierBillId === String(b.id) ? '1px solid #bae6fd' : '1px solid #e2e8f0', background: selectedSupplierBillId === String(b.id) ? '#f0f9ff' : '#fff', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Inv: {b.invoice_no || `ID:${b.id}`}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--mod-text-muted)' }}>{formatWhen(b.date)}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ color: '#dc2626', fontWeight: 700, fontSize: '0.95rem' }}>₹{Number(b.balance || 0).toLocaleString("en-IN")}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--mod-text-muted)' }}>Total: ₹{Number(b.total || 0).toLocaleString("en-IN")}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: 'var(--mod-text-main)' }}>Process Payment</h3>
                {selectedSupplierBill ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Amount (₹)</label>
                                <input style={inputStyle} type="number" placeholder={`Max ₹${selectedSupplierBill.balance}`} value={supplierPayAmount} onChange={(e) => setSupplierPayAmount(e.target.value)} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Mode</label>
                                <select style={inputStyle} value={supplierPayMode} onChange={(e) => setSupplierPayMode(e.target.value)}>
                                    <option value="Cash">Cash</option><option value="UPI">UPI</option><option value="Bank">Bank</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Receipt / UTR (Optional)</label>
                            <input style={inputStyle} value={supplierReceipt} onChange={(e) => setSupplierReceipt(e.target.value)} placeholder="e.g. TXN12345" />
                        </div>
                        <button style={{ padding: '12px', borderRadius: '8px', background: 'linear-gradient(135deg, var(--mod-sky), #3b82f6)', color: '#fff', border: 'none', fontWeight: 700, cursor: (payingSupplier || !supplierPayAmount) ? 'not-allowed' : 'pointer', opacity: (payingSupplier || !supplierPayAmount) ? 0.6 : 1 }} onClick={() => void handleSupplierPayment()}>
                            {payingSupplier ? "Processing..." : `Pay Bill #${selectedSupplierBill.invoice_no || selectedSupplierBill.id}`}
                        </button>
                    </div>
                ) : (
                    <div className="mod-empty-state" style={{ height: '200px', border: 'none', background: '#f8fafc' }}>
                        <span style={{ fontSize: '1.5rem' }}>🧾</span>
                        <p style={{ marginTop: '8px', fontSize: '0.9rem' }}>Select a pending bill to initiate payment.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
